/**
 * Ветка подтверждения записи внутри общего OKDoc flow-router.
 * Дата: 17.03.2026
 */
import { Button } from '@/components/ui';
import { AppointmentSummaryCard } from '@/features/okdoc/components/AppointmentSummaryCard';
import type { OkdocBrandTheme } from '@/features/okdoc/config';
import {
  getAppointmentActionLabel,
  hasScenarioHeroCopy,
  resolveAppointmentSubmitActions,
  resolvePublicScenarioProfile,
  resolveScenarioResultCopy,
} from '@/features/okdoc/utils/scenarioProfile';
import type { PublicAppointmentActionLanding } from '@/features/okdoc/types';

interface AppointmentConfirmationLandingProps {
  landing: PublicAppointmentActionLanding;
  theme: OkdocBrandTheme;
  submitStatus: string;
  submitAction: string;
  submitError: string;
  onAction: (action: string) => void;
}

// 17.03.26: отдельная ветка подтверждения записи после выноса общего public router.
export function AppointmentConfirmationLanding({
  landing,
  theme,
  submitStatus,
  submitAction,
  submitError,
  onAction,
}: AppointmentConfirmationLandingProps) {
  const profile = resolvePublicScenarioProfile(landing);
  const submitActions = resolveAppointmentSubmitActions(profile);
  const confirmLabel = submitActions.find((item) => item.action === 'confirm')?.label || 'Приду';
  const rescheduleLabel = submitActions.find((item) => item.action === 'reschedule')?.label || 'Перенести';
  const cancelLabel = submitActions.find((item) => item.action === 'cancel')?.label || 'Отменить';
  const successResultCopy = resolveScenarioResultCopy(profile, submitAction);
  const quietSecondaryActionClass =
    'w-full rounded-2xl border border-slate-200 bg-white px-5 py-4 text-base text-slate-800 shadow-sm hover:bg-slate-50 focus:ring-slate-300';
  const quietDangerActionClass =
    'w-full rounded-2xl border border-rose-200 bg-white px-5 py-4 text-base text-slate-800 shadow-sm hover:bg-rose-50 focus:ring-rose-200';

  return (
    <>
      {landing.status === 'used' || submitStatus === 'used' ? (
        <div className="rounded-[28px] border border-sky-100 bg-sky-50 p-8 text-center text-sky-800">
          Ответ уже зафиксирован{landing.used_action ? `: ${getAppointmentActionLabel(landing.used_action)}` : ''}.
        </div>
      ) : (
        <>
          {hasScenarioHeroCopy(profile) && (
            <div className="mb-6 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_24px_60px_-36px_rgba(15,23,42,0.35)]">
              {profile?.badge && <p className="text-sm font-medium text-slate-500">{profile.badge}</p>}
              {profile?.title && <h2 className="mt-1 text-xl font-semibold text-slate-900">{profile.title}</h2>}
              {(profile?.subtitle || profile?.description) && (
                <p className="mt-4 text-sm leading-6 text-slate-600">
                  {profile?.subtitle ? `${profile.subtitle}. ` : ''}
                  {profile?.description}
                </p>
              )}
            </div>
          )}

          <AppointmentSummaryCard landing={landing} />

          {submitStatus === 'success' ? (
            <div className="mt-6 rounded-[28px] border border-emerald-100 bg-emerald-50 p-8 text-center text-emerald-800">
              {successResultCopy?.title || `Спасибо! Ответ «${getAppointmentActionLabel(submitAction)}» сохранён.`}
              {successResultCopy?.description && (
                <div className="mt-2 text-sm leading-6 text-emerald-800">{successResultCopy.description}</div>
              )}
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              <Button
                className={`w-full rounded-2xl px-5 py-4 text-base shadow-sm ${theme.primaryButton}`}
                size="lg"
                onClick={() => onAction('confirm')}
                isLoading={submitStatus === 'loading' && submitAction === 'confirm'}
              >
                {confirmLabel}
              </Button>
              <Button
                className={quietSecondaryActionClass}
                size="lg"
                variant="secondary"
                onClick={() => onAction('reschedule')}
                isLoading={submitStatus === 'loading' && submitAction === 'reschedule'}
              >
                {rescheduleLabel}
              </Button>
              <Button
                className={quietDangerActionClass}
                size="lg"
                variant="secondary"
                onClick={() => onAction('cancel')}
                isLoading={submitStatus === 'loading' && submitAction === 'cancel'}
              >
                {cancelLabel}
              </Button>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-4 rounded-2xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
              {submitError || 'Не удалось отправить ответ.'}
            </div>
          )}
        </>
      )}
    </>
  );
}
